
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { 
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@/components/ui/tabs";
import { useAccountPlanning } from "@/hooks/useAccountPlanning";
import { AccountPlan } from "@/types/growth.types";
import { useAuth } from "@/contexts/AuthContext";

interface AccountPlanFormProps {
  organizationId: string;
  planId?: string;
  onSuccess?: () => void;
  defaultValues?: Partial<AccountPlan>;
  year?: number;
  quarter?: number;
}

export function AccountPlanForm({
  organizationId,
  planId,
  onSuccess,
  defaultValues,
  year = new Date().getFullYear(),
  quarter = Math.floor(new Date().getMonth() / 3) + 1
}: AccountPlanFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createAccountPlan, updateAccountPlan, getAccountPlan } = useAccountPlanning();
  
  // If planId is provided, fetch the plan details
  const { data: existingPlan, isLoading: isLoadingPlan } = 
    planId ? getAccountPlan(planId) : { data: undefined, isLoading: false };

  // Initialize form with default values or fetched plan
  const form = useForm<Partial<AccountPlan>>({
    defaultValues: existingPlan || defaultValues || {
      organization_id: organizationId,
      plan_year: year,
      plan_quarter: quarter,
      status: 'draft',
      priority_level: 'medium',
      created_by: user?.id,
    }
  });

  // Update form when existing plan is fetched
  useEffect(() => {
    if (existingPlan && !form.formState.isDirty) {
      form.reset(existingPlan);
    }
  }, [existingPlan, form]);

  // Handle form submission
  const onSubmit = async (data: Partial<AccountPlan>) => {
    try {
      setIsSubmitting(true);
      
      if (planId) {
        // Update existing plan
        await updateAccountPlan({
          id: planId,
          data: {
            ...data,
            updated_at: new Date().toISOString()
          }
        });
        toast({
          title: "Account plan updated",
          description: "The account plan has been updated successfully."
        });
      } else {
        // Create new plan
        await createAccountPlan({
          ...data,
          organization_id: organizationId,
          created_by: user?.id
        });
        toast({
          title: "Account plan created",
          description: "The account plan has been created successfully."
        });
      }
      
      if (onSuccess) onSuccess();
      
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingPlan) {
    return <div className="p-4 text-center">Loading plan details...</div>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <Tabs defaultValue="basic">
          <TabsList className="mb-4">
            <TabsTrigger value="basic">Basic Information</TabsTrigger>
            <TabsTrigger value="goals">Goals & Strategy</TabsTrigger>
            <TabsTrigger value="execution">Execution Plan</TabsTrigger>
          </TabsList>
          
          <TabsContent value="basic">
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name="plan_year"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Year</FormLabel>
                          <FormControl>
                            <Input type="number" {...field} value={field.value || year} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="plan_quarter"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Quarter</FormLabel>
                          <Select
                            value={String(field.value || quarter)}
                            onValueChange={(value) => field.onChange(parseInt(value))}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select quarter" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="1">Q1</SelectItem>
                              <SelectItem value="2">Q2</SelectItem>
                              <SelectItem value="3">Q3</SelectItem>
                              <SelectItem value="4">Q4</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name="priority_level"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Priority Level</FormLabel>
                          <Select
                            value={field.value || 'medium'}
                            onValueChange={field.onChange}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select priority" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="low">Low</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select
                            value={field.value || 'draft'}
                            onValueChange={field.onChange}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="draft">Draft</SelectItem>
                              <SelectItem value="in_review">In Review</SelectItem>
                              <SelectItem value="approved">Approved</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <FormField
                    control={form.control}
                    name="revenue_target"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Revenue Target ($)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="growth_target_percentage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Growth Target (%)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="goals">
            <Card>
              <CardContent className="pt-6 space-y-6">
                <FormField
                  control={form.control}
                  name="strategic_goals"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Strategic Goals</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Enter strategic goals for this quarter..."
                          className="min-h-[120px]"
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormDescription>
                        Define the key strategic objectives for this client this quarter.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="whitespace_assessment"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Whitespace Assessment</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Analyze whitespace opportunities..."
                          className="min-h-[120px]"
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormDescription>
                        Identify untapped opportunities and potential areas for expansion.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="challenges"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Challenges & Risks</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Identify potential challenges..."
                          className="min-h-[120px]"
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="execution">
            <Card>
              <CardContent className="pt-6 space-y-6">
                <FormField
                  control={form.control}
                  name="opportunities_summary"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Opportunity Summary</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Summarize key opportunities..."
                          className="min-h-[120px]"
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="key_initiatives"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Key Initiatives</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="List key initiatives for this quarter..."
                          className="min-h-[120px]"
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormDescription>
                        Outline specific actions and initiatives planned for this quarter.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="success_metrics"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Success Metrics</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Define how success will be measured..."
                          className="min-h-[120px]"
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormDescription>
                        Clear metrics to evaluate the success of this quarterly plan.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onSuccess && onSuccess()}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : existingPlan ? "Update Plan" : "Create Plan"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
